import React, { useEffect } from 'react';
import { useFieldArray } from 'react-hook-form';
import { Button } from 'baseui/button';
import { useStyletron } from 'baseui';
import _get from 'lodash/get';

import { useDependencies } from '../../customHooks';
import RowRenderer from './RowRenderer';
import ErrorMessage from '../ErrorMessage';
import { ERROR_SEVERITY } from '../../constants';

interface IMultiRowRendererProps {
	fieldConfig: any;
	layoutProps: any;
	path: any;
	control: any;
	errors: any;
	mode: any;
	watch: any;
	setShowLoader: any;
	defaultValue?: any;
	setValue: any;
	initialValues?: any;
}

const useMultiRowRenderer = ({
	fieldConfig,
	layoutProps,
	path,
	control,
	errors,
	mode,
	watch,
	setValue,
	setShowLoader,
	initialValues
}: IMultiRowRendererProps) => {
	const name = path.join('.');
	const error = _get(errors, name);

	const {
		fields: arrayFields,
		append,
		prepend,
		remove
	} = useFieldArray({
		control,
		name
	});

	const onAddRowClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
		append({});
		event.preventDefault();
	};

	const renderProps = useDependencies({
		...fieldConfig[layoutProps.key].getRenderProps(),
		watch,
		remove,
		append,
		prepend,
		arrayFields,
		mode,
		name,
		setValue
	});
	const disabled = !_get(renderProps, ['isEditable', mode], true);
	const maxLength = _get(renderProps, 'maxLength', Number.MAX_SAFE_INTEGER);
	const minLength = _get(renderProps, 'minLength', 1);

	useEffect(() => {
		if (arrayFields.length > maxLength) {
			const indicesToBeRemoved = Array.from({ length: maxLength }, (_, i) => i);
			remove(indicesToBeRemoved);
		}
	}, [remove, append, arrayFields]);

	return {
		fieldConfig,
		layoutProps,
		path,
		arrayFields,
		control,
		errors,
		mode,
		disabled,
		maxLength,
		minLength,
		watch,
		onAddRowClick,
		remove,
		setShowLoader,
		setValue,
		currError: error,
		initialValues
	};
};

const MultiRowRenderer: React.FC<IMultiRowRendererProps> = (props) => {
	const {
		fieldConfig,
		layoutProps,
		path,
		arrayFields,
		control,
		errors,
		mode,
		disabled,
		maxLength,
		minLength,
		watch,
		onAddRowClick,
		remove,
		setShowLoader,
		setValue,
		currError,
		initialValues
	} = useMultiRowRenderer(props);
	const [css] = useStyletron();

	return (
		<div className={css({ display: 'flex', flexDirection: 'column', gap: '10px' })}>
			{currError?.message && <ErrorMessage severity={ERROR_SEVERITY.ERROR} message={currError.message} />}
			{arrayFields.map((arrayField, index) => {
				return (
					<div key={arrayField.id}>
						<RowRenderer
							layoutProps={{ ...layoutProps, key: `${layoutProps.getKey()}-${index}` }}
							fieldConfig={fieldConfig}
							control={control}
							errors={errors}
							setShowLoader={setShowLoader}
							path={[`${path.join('.')}[${index}]`]}
							defaultValue={{ field: arrayField, path: [] }}
							mode={mode}
							watch={watch}
							setValue={setValue}
							initialValues={initialValues}
						/>
						{!disabled && !(arrayFields.length <= minLength) && (
							<div className={css({ margin: '14px 50px' })}>
								<Button
									kind='secondary'
									onClick={() => {
										remove(index);
									}}>
									Delete Row
								</Button>
							</div>
						)}
					</div>
				);
			})}
			{!disabled && !(arrayFields.length >= maxLength) && (
				<div className={css({ margin: '14px 0' })}>
					<Button onClick={onAddRowClick} disabled={disabled || arrayFields.length >= maxLength}>
						Add Row
					</Button>
				</div>
			)}
		</div>
	);
};

export default MultiRowRenderer;
